import { useState, type ReactNode } from "react";
import {
  buildCharacterFromTemplate,
  CHARACTER_TEMPLATES,
  type Character,
  type CharacterTemplate,
  type CharacterTemplateField,
} from "@roqeet/core";
import { Map, Sparkles, UserRound, VenetianMask, X } from "lucide-react";
import { storage } from "../../state/storage.js";
import { useT } from "../../i18n/use-t.js";
import { useEscapeToClose } from "../use-escape-to-close.js";

export function CharacterTemplateWizard({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: (character: Character, startChat: boolean) => void | Promise<void>;
}) {
  const { t } = useT();
  const [selected, setSelected] = useState<CharacterTemplate | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(onClose, !busy);

  const pick = (template: CharacterTemplate) => {
    const seeded: Record<string, string> = {};
    for (const field of template.fields) seeded[field.key] = field.default ?? "";
    setValues(seeded);
    setSelected(template);
    setError(null);
  };

  const create = async (startChat: boolean) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const character = buildCharacterFromTemplate(selected, values);
      await storage.characters.put(character);
      await onCreated?.(character, startChat);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-screen max-h-[100dvh] w-full max-w-lg flex-col overflow-hidden bg-bg sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:border sm:border-line">
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <div className="flex min-w-0 items-center gap-2">
            {selected && (
              <button
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-faint hover:bg-bg-raised hover:text-ink"
                onClick={() => setSelected(null)}
                aria-label="템플릿 목록으로 돌아가기"
                disabled={busy}
              >
                ‹
              </button>
            )}
            <h2 className="truncate text-sm font-semibold text-ink">
              {selected ? selected.title : "롤플레잉 캐릭터 템플릿"}
            </h2>
          </div>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-md text-ink-faint hover:bg-bg-raised hover:text-ink"
            onClick={onClose}
            aria-label={t("misc.close")}
            disabled={busy}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>

        {!selected ? (
          <TemplateList onPick={pick} />
        ) : (
          <>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 scroll-touch">
              <p className="text-xs leading-5 text-ink-faint">{selected.description}</p>
              <div className="rounded-lg border border-line bg-bg-soft p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-ink">
                  <UserRound size={15} aria-hidden className="text-accent-soft" />
                  몇 칸만 채우면 저장 가능한 캐릭터 카드가 만들어집니다.
                </div>
                <p className="text-xs leading-5 text-ink-faint">
                  대사와 서사는 LLM이 맡고, 상태 계산이 필요한 게임 규칙은 RoQeet 게임 템플릿에서
                  런타임이 처리하도록 분리해 두었습니다.
                </p>
              </div>
              {selected.fields.map((field) => (
                <TemplateField
                  key={field.key}
                  field={field}
                  value={values[field.key] ?? ""}
                  onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))}
                />
              ))}
              {error && (
                <div className="rounded-lg border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                  {error}
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line p-3">
              <button
                className="rounded-lg px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised disabled:opacity-50"
                onClick={() => create(false)}
                disabled={busy}
              >
                캐릭터만 만들기
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
                onClick={() => create(true)}
                disabled={busy}
              >
                <Sparkles size={16} aria-hidden />
                만들고 대화 시작
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TemplateList({ onPick }: { onPick: (template: CharacterTemplate) => void }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
      <p className="px-1 pb-2 text-xs leading-5 text-ink-faint">
        이름, 성격, 관계, 첫 장면처럼 초보자가 바로 떠올릴 수 있는 정보만 넣어도
        롤플레잉 채팅을 시작할 수 있습니다.
      </p>
      <ul className="space-y-2">
        {CHARACTER_TEMPLATES.map((template) => (
          <li key={template.id}>
            <button
              className="flex w-full items-start gap-3 rounded-xl border border-line bg-bg-soft px-3 py-3 text-left hover:border-accent hover:bg-bg-raised"
              onClick={() => onPick(template)}
            >
              <span className="mt-0.5" aria-hidden>
                {iconFor(template.icon)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-ink">{template.title}</span>
                <span className="mt-0.5 block text-xs leading-5 text-ink-faint">
                  {template.description}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TemplateField({
  field,
  value,
  onChange,
}: {
  field: CharacterTemplateField;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink">{field.label}</span>
      {field.multiline ? (
        <textarea
          className="min-h-[74px] w-full resize-y rounded-lg border border-line bg-bg-soft px-3 py-2 text-sm text-ink outline-none focus:border-accent"
          value={value}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          className="w-full rounded-lg border border-line bg-bg-soft px-3 py-2 text-sm text-ink outline-none focus:border-accent"
          value={value}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </label>
  );
}

function iconFor(icon: CharacterTemplate["icon"], className = "h-5 w-5 text-accent"): ReactNode {
  switch (icon) {
    case "map":
      return <Map className={className} />;
    case "theater":
      return <VenetianMask className={className} />;
    case "sparkles":
    default:
      return <Sparkles className={className} />;
  }
}
